import React, { useCallback, useState, useMemo } from 'react';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin';
import { showConfirm } from '@/utils/common/alert';
import { useEditUserMutation, useAssignRolesMutation, useRevokeRoleMutation, useDeleteUserMutation } from '@/store/api/admin/userManagementApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useErrorToast, useToast } from '@/contexts/admin/ToastContext';
import { roleNamesAndDescriptions } from '@/types/admin/userManagement';
import { IOrganization, IProperty } from '@/types/admin/navigation';
import {
  UserAccountInfoSection,
  UserRoleSelectionSection,
  UserPreviewSection,
  CreateUserSuccessModal,
} from '@/components';
import { useEditUserForm } from '@/components/admin/userManagement/CreateUser/hooks/useEditUserForm';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';

type EditUserScreenProps = StackScreenProps<AdminStackParamList, 'EditUser'>;

const FORM_STEPS = [
  'Edit Account',
  'Assign User Permissions',
  'Preview'
];

export const EditUserScreen: React.FC<EditUserScreenProps> = ({
  navigation,
  route,
}) => {
  const { user } = route.params;
  const { showApiError } = useErrorToast();
  const { showSuccess } = useToast();
  const adminUser = useAdminUser();

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // API mutations
  const [editUser, { isLoading: isEditUserLoading }] = useEditUserMutation();
  const [assignUserRoles, { isLoading: isAssignUserLoading }] = useAssignRolesMutation();
  const [revokeRole, { isLoading: isRevokeRoleLoading }] = useRevokeRoleMutation();
  const [deleteUser, { isLoading: isDeleteUserLoading }] = useDeleteUserMutation();

  const isLoading = isEditUserLoading || isAssignUserLoading || isRevokeRoleLoading || isDeleteUserLoading;

  // Custom hook for form logic
  const {
    formData,
    errors,
    activeStep,
    handleFieldChange,
    handleNext,
    handleBack,
    validateForm,
    showDiscardAlert,
  } = useEditUserForm(user);

  // Cancel handler
  const handleCancel = useCallback(() => {
    showDiscardAlert(() => navigation.goBack());
  }, [showDiscardAlert, navigation]);

  // Delete user handler
  const handleDelete = useCallback(async () => {
    const firstConfirmed = await showConfirm(
      DESKTOP_CONFIRM_COPY.deleteUser.title,
      DESKTOP_CONFIRM_COPY.deleteUser.message,
      DESKTOP_CONFIRM_COPY.deleteUser.confirmText,
      DESKTOP_CONFIRM_COPY.deleteUser.cancelText,
    );
    if (!firstConfirmed) return;

    const finalConfirmed = await showConfirm(
      DESKTOP_CONFIRM_COPY.deleteUser.finalTitle,
      DESKTOP_CONFIRM_COPY.deleteUser.finalMessage,
      DESKTOP_CONFIRM_COPY.deleteUser.finalConfirmText,
      DESKTOP_CONFIRM_COPY.deleteUser.finalCancelText,
    );
    if (!finalConfirmed) return;

    try {
      // First revoke all roles
      if (user.userType && user.userType !== 'No Role') {
        // Get current user's organizations and properties
        const currentOrgs = (adminUser.organizations || []).filter((org: IOrganization) =>
          user.organization?.includes(org.name)
        );
        const currentProps = (adminUser.properties || []).filter((prop: IProperty) =>
          user.properties?.includes(prop.marketingName)
        );

        // Revoke roles for each organization/property
        for (const org of currentOrgs) {
          await revokeRole({
            email: user.email,
            organizationId: org.id,
          }).unwrap();
        }

        for (const prop of currentProps) {
          await revokeRole({
            email: user.email,
            propertyId: prop.id,
          }).unwrap();
        }

        // For super admin, revoke without org/property
        if (user.userType === roleNamesAndDescriptions.superAdmin.value) {
          await revokeRole({
            email: user.email,
          }).unwrap();
        }
      }

      // Then delete the user
      await deleteUser({ accountId: user.id }).unwrap();

      // Show success toast
      showSuccess('User deleted successfully!');
      navigation.goBack();
    } catch (error: any) {
      console.error('Delete user error:', error);
      showApiError(error, 'Failed to delete user');
    }
  }, [user, adminUser, revokeRole, deleteUser, showApiError, showSuccess, navigation]);

  // Form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    try {
      // Step 1: Update user account info
      const editResponse = await editUser({
        accountId: user.id,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
      }).unwrap();

      if (editResponse.success) {
        // Step 2: Update roles if changed (following admin-web pattern)
        if (formData.roleName !== user.userType) {
          // Assign new roles (the API handles replacing existing roles)
          const foundProperties = (adminUser.properties || []).filter((prop: IProperty) =>
            formData.properties?.some((selectedProp: string) => prop.marketingName === selectedProp)
          );
          const foundOrgs = (adminUser.organizations || []).filter((org: IOrganization) =>
            formData.organizations?.some((selectedOrg: string) => org.name === selectedOrg)
          );

          // Prepare the API call based on role type
          const apiPayload: any = {
            email: formData.email,
            roleName: formData.roleName,
          };

          // Only include IDs for roles that need them - avoid empty arrays due to backend bug
          if (formData.roleName === roleNamesAndDescriptions.orgAdmin.value) {
            const orgIds = foundOrgs.map((org: IOrganization) => org.id);
            if (orgIds.length > 0) {
              apiPayload.orgIds = orgIds;
              // Don't include propertyIds at all for org admin
            } else {
              throw new Error('Organization Admin role requires at least one organization to be selected');
            }
          } else if ([
            roleNamesAndDescriptions.propertyAdmin.value,
            roleNamesAndDescriptions.billingAdmin.value,
          ].includes(formData.roleName)) {
            const propertyIds = foundProperties.map((prop: IProperty) => prop.id);
            if (propertyIds.length > 0) {
              apiPayload.propertyIds = propertyIds;
              // Don't include orgIds at all for property/billing admin
            } else {
              throw new Error('Property/Billing Admin role requires at least one property to be selected');
            }
          } else if (formData.roleName === roleNamesAndDescriptions.superAdmin.value) {
            // Super admin doesn't need any IDs - don't include them at all
          }

          await assignUserRoles(apiPayload).unwrap();
        }

        // Show success toast
        showSuccess('User updated successfully!');
        setShowSuccessModal(true);
      } else {
        throw new Error('Failed to update user account');
      }
    } catch (error: any) {
      console.error('Edit user error:', error);
      showApiError(error, 'Failed to update user');
    }
  }, [
    formData,
    user,
    adminUser.properties,
    adminUser.organizations,
    editUser,
    assignUserRoles,
    revokeRole,
    validateForm,
    showApiError,
    showSuccess,
  ]);

  // Success modal close handler
  const handleSuccessClose = useCallback(() => {
    setShowSuccessModal(false);
    navigation.goBack();
  }, [navigation]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => {
    switch (activeStep) {
      case 0:
        return (
          <UserAccountInfoSection
            formData={formData}
            errors={errors}
            onFieldChange={handleFieldChange}
            isEditMode={true}
          />
        );
      case 1:
        return (
          <UserRoleSelectionSection
            formData={formData}
            errors={errors}
            onFieldChange={handleFieldChange}
            adminUser={adminUser}
          />
        );
      case 2:
        return (
          <UserPreviewSection
            formData={formData}
          />
        );
      default:
        return null;
    }
  }, [activeStep, formData, errors, handleFieldChange, adminUser]);

  return (
    <>
      <MobileFormScreen
        screenName="EditUser"
        navigation={navigation}
        isSubmitting={isLoading}
        submitText={activeStep === FORM_STEPS.length - 1 ? 'Update' : 'Next'}
        cancelText={activeStep > 0 ? 'Back' : 'Cancel'}
        onSubmit={activeStep === FORM_STEPS.length - 1 ? handleSubmit : handleNext}
        onCancel={activeStep > 0 ? handleBack : handleCancel}
        showDelete
        onDelete={handleDelete}
      >
        {scrollContent}
      </MobileFormScreen>

      {/* Success Modal */}
      <CreateUserSuccessModal
        visible={showSuccessModal}
        onClose={handleSuccessClose}
        userName={`${formData.firstName} ${formData.lastName}`}
      />
    </>
  );
};
